import React, { useContext, useMemo } from 'react';

import cx from 'classnames';

import { Label } from 'components/shared/Label';
import Text from 'components/shared/Typography/Text';
import { EOrganizedBy } from 'detailsv2/structure';
import i18n from 'i18n';
import { getDateFormat } from 'utils/date';
import { formatDatesRangeWithoutParseISO } from 'utils/itineraryDetails';

import {
  IRooms,
  ITravelBriefAccommodation,
  TDateRange,
  TNightsCount,
  WithValue,
} from '../../../../../LandingV2/Page/RightSide/blocks/Overview/TravelBriefTable/types';
import { PDFDataContext } from '../../../../context';
import s from '../index.module.css';

export function useAccommodationTable() {
  const { page } = useContext(PDFDataContext);
  const { accommodations: days } = page.travelBrief;
  const { settings } = page;

  const columns = useMemo(
    () => [
      {
        Header: i18n.t('proposal_content.travel_brief.tabs.overview.tabs.day'),
        accessor: 'dates',
        fixedSize: '17%',
        className: cx(s.thDay, 'custom-manifest-thDate'),
        Cell: ({ value }: WithValue<TDateRange>) => {
          return (
            <div>
              {page.scheduleType === EOrganizedBy.Day ? (
                <>
                  {i18n.t('proposal_content.travel_brief.tabs.overview.tabs.day')} {value.daysRange.start}{' '}
                  {value.daysRange.end && value.daysRange.start !== value.daysRange.end
                    ? `- ${value.daysRange.end}`
                    : null}
                </>
              ) : (
                <>
                  {formatDatesRangeWithoutParseISO(
                    { start: value.datesRange.start, end: value.datesRange.end },
                    {
                      includeOnlyDifferentYears: false,
                      translate: true,
                      translationDateFormat: getDateFormat(settings?.displayDatesDdMmYyyy),
                    },
                  )}
                </>
              )}
            </div>
          );
        },
      },
      {
        Header: i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.accommodation'),
        accessor: 'accommodation',
        fixedSize: '33%',
        className: cx(s.thAcc, 'custom-manifest-thAcc'),
        Cell: ({ value }: WithValue<ITravelBriefAccommodation>) => {
          const { name, isAlternate } = value;
          return (
            <div className={s.eventContainer}>
              {name}
              {isAlternate ? (
                <span className={s.labelContainer}>
                  <Label
                    color="#65ADFF"
                    text={i18n.t('proposal_content.event_details.tags.alternate')}
                    className={s.alternateLabel}
                  />
                </span>
              ) : (
                ''
              )}
            </div>
          );
        },
      },
      {
        Header: i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.details'),
        accessor: 'rooms',
        fixedSize: '37%',
        className: cx(s.thRoom, 'custom-manifest-thAcc'),
        Cell: ({ value }: WithValue<IRooms>) => {
          return (
            <div className={s.eventContainer}>
              {value.rooming?.map((room, i) => {
                return (
                  <div key={room.name + i} style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>
                      {room.name ? room.name : ''} {room.count ? `x ${room.count}` : ''}
                    </span>
                  </div>
                );
              })}
              {value.roomBasis ? (
                // size="small" doesn't work correct when printing bc of rem
                <Text italic style={{ fontSize: '14px' }}>
                  {value.roomBasis}
                </Text>
              ) : (
                '-'
              )}
            </div>
          );
        },
      },
      {
        Header: i18n.t('proposal_content.travel_brief.tabs.accommodation.tabs.nights'),
        accessor: 'nights',
        fixedSize: '13%',
        className: cx(s.thNights, 'custom-manifest-thAcc'),
        Cell: ({ value }: WithValue<TNightsCount>) => {
          return <div className={s.nights}>{value || '-'}</div>;
        },
      },
    ],
    [page.scheduleType, i18n.version, settings?.displayDatesDdMmYyyy],
  );

  return { days, columns };
}
